// Medical Theme Design System
// Professional, clean, and trustworthy colors for healthcare applications

import { ThemeOptions } from '@mui/material/styles';

export const medicalTheme: ThemeOptions = {
  palette: {
    // Primary Medical Blue - Trust, professionalism, healthcare
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#0d47a1',
      contrastText: '#ffffff',
    },
    
    // Secondary Medical Green - Health, growth, safety
    secondary: {
      main: '#4caf50',
      light: '#66bb6a',
      dark: '#1b5e20',
      contrastText: '#ffffff',
    },
    
    // Medical Red - Urgency, alerts, critical
    error: {
      main: '#f44336',
      light: '#ef5350',
      dark: '#c62828',
      contrastText: '#ffffff',
    },
    
    // Medical Orange - Warning, attention, caution
    warning: {
      main: '#ff9800',
      light: '#ffa726',
      dark: '#e65100',
      contrastText: '#ffffff',
    },
    
    // Medical Purple - Premium, innovation, technology
    info: {
      main: '#9c27b0',
      light: '#ab47bc',
      dark: '#4a148c',
      contrastText: '#ffffff',
    },
    
    // Success Green - Success, completion, positive
    success: {
      main: '#4caf50',
      light: '#66bb6a',
      dark: '#1b5e20',
      contrastText: '#ffffff',
    },
    
    // Neutral Grays - Professional, clean, readable
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    
    // Text Colors
    text: {
      primary: '#212121',
      secondary: '#757575',
      disabled: '#bdbdbd',
    },
    
    // Background Colors
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    
    // Divider
    divider: '#e0e0e0',
  },
  
  // Typography - Clean, readable, professional
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
  },
  
  // Shape - Rounded corners for modern look
  shape: {
    borderRadius: 8,
  },
  
  // Spacing - Consistent spacing scale
  spacing: 8,
  
  // Shadows - Subtle, professional shadows
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.15)',
    '0px 20px 40px rgba(0, 0, 0, 0.18)',
    '0px 24px 48px rgba(0, 0, 0, 0.2)',
    '0px 28px 56px rgba(0, 0, 0, 0.22)',
    '0px 32px 64px rgba(0, 0, 0, 0.25)',
    '0px 36px 72px rgba(0, 0, 0, 0.28)',
    '0px 40px 80px rgba(0, 0, 0, 0.3)',
    '0px 44px 88px rgba(0, 0, 0, 0.32)',
    '0px 48px 96px rgba(0, 0, 0, 0.35)',
    '0px 52px 104px rgba(0, 0, 0, 0.38)',
    '0px 56px 112px rgba(0, 0, 0, 0.4)',
    '0px 60px 120px rgba(0, 0, 0, 0.42)',
    '0px 64px 128px rgba(0, 0, 0, 0.45)',
    '0px 68px 136px rgba(0, 0, 0, 0.48)',
    '0px 72px 144px rgba(0, 0, 0, 0.5)',
    '0px 76px 152px rgba(0, 0, 0, 0.52)',
    '0px 80px 160px rgba(0, 0, 0, 0.55)',
    '0px 84px 168px rgba(0, 0, 0, 0.58)',
    '0px 88px 176px rgba(0, 0, 0, 0.6)',
    '0px 92px 184px rgba(0, 0, 0, 0.62)',
  ],
};

// Medical Icons and Assets
export const medicalIcons = {
  // Primary Icons
  stethoscope: '🩺',
  heart: '❤️',
  brain: '🧠',
  pill: '💊',
  syringe: '💉',
  thermometer: '🌡️',
  bandage: '🩹',
  medicalBag: '🏥',
  
  // Status Icons
  checkmark: '✅',
  cross: '❌',
  warning: '⚠️',
  info: 'ℹ️',
  
  // User Types
  doctor: '👨‍⚕️',
  nurse: '👩‍⚕️',
  patient: '🤒',
  admin: '👨‍💼',
};

// Medical Color Utilities
export const medicalColors = {
  // Status Colors
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196f3',
  
  // Medical Specific Colors
  blood: '#d32f2f',
  healthy: '#4caf50',
  critical: '#f44336',
  stable: '#2196f3',
  recovery: '#4caf50',
  
  // Background Gradients
  primaryGradient: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
  secondaryGradient: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
  errorGradient: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
  warningGradient: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
  
  // Glass Effect
  glassBackground: 'rgba(255, 255, 255, 0.95)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',
};

export default medicalTheme;